import React from "react";
import { Redirect, useLocation } from "react-router-dom";
import {
  useGetGlobalUserStatusDetailsQuery,
  useGetSessionStatusQuery,
} from "common/types/mainHooks";
import { Loader } from "@jsq/ds/core";
import { getBaseUrl } from "../AccountActivation/utils";
import { useAppContext } from "../AppContext";
import { getRedirectUrl } from "./utils";

/**
 * Wrapper component to handle any required redirects related to the P2.0 activation flow.
 * Sends user to 2FA setup page, legacy 2FA verification page, user account linking page, or
 * new managers list page as needed. If no redirect is necessary, display children components
 * Use PrivateRoute with the isActivationProtected props for login protected components
 */

export const ActivationProtectedRoute: React.FC = ({ children }) => {
  const { user } = useAppContext();
  const location = useLocation<Record<string, unknown>>();
  const { data: sessionData } = useGetSessionStatusQuery({
    skip: !user,
  });
  const { data: userData } = useGetGlobalUserStatusDetailsQuery({
    skip: !user,
  });
  const searchParams = new URLSearchParams(location.search);

  if (user && !userData) {
    return <Loader loading />;
  }

  const currentUser = userData?.currentGlobalUser;
  if (currentUser) {
    const isSessionSpoofed = sessionData?.session?.spoofed || false;
    const baseUrl = getBaseUrl(location.pathname);
    const redirectUrl = getRedirectUrl(currentUser, baseUrl, isSessionSpoofed);
    const strippedPath = location.pathname.startsWith(`${baseUrl}/`)
      ? location.pathname.replace(`${baseUrl}/`, "")
      : location.pathname.replace(`${baseUrl}`, "");
    searchParams.append("path", `${strippedPath}${encodeURIComponent(location.search)}`);
    const shouldRefetchQueries = Boolean(location.state?.shouldRefetchQueries);
    if (redirectUrl) {
      return (
        <Redirect
          to={{
            pathname: redirectUrl,
            search: `?${searchParams.toString()}`,
            ...(shouldRefetchQueries && { state: { shouldRefetchQueries } }),
          }}
        />
      );
    }
  }

  return <>{children}</>;
};

export default ActivationProtectedRoute;
